import React, { useState } from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, DatabaseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

interface ExecuteSqlQueryToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

const ExecuteSqlQueryToolCallComponent: React.FC<ExecuteSqlQueryToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Parse SQL query from args
  let sqlQuery = '';
  try {
    const parsed = typeof args === 'object' && args ? args : JSON.parse(argsText);
    sqlQuery = parsed?.sql_query || '';
  } catch {
    sqlQuery = '';
  }

  // Truncate query for display
  const queryPreview = sqlQuery.replace(/\s+/g, ' ').trim();
  const shortQuery = queryPreview.length > 80 ? queryPreview.substring(0, 80) + '...' : queryPreview;

  // Parse result
  let isSuccess = false;
  let rowCount = 0;
  let columnCount = 0;
  let isTruncated = false;
  let errorMessage = '';
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      rowCount = parsed?.row_count || 0;
      columnCount = parsed?.columns?.length || 0;
      isTruncated = parsed?.truncated === true;
      if (!isSuccess) {
        errorMessage = parsed?.error || 'Query failed';
      }
    } catch {
      isSuccess = false;
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning
          ? 'bg-primary/10 border-b border-primary/20'
          : 'bg-muted/50 border-b border-border'
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Executing SQL query...
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <DatabaseIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    SQL Query
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    {errorMessage || 'Query failed to execute'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <DatabaseIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    SQL Query
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Retrieved {rowCount} row{rowCount !== 1 ? 's' : ''}{columnCount > 0 ? ` across ${columnCount} column${columnCount !== 1 ? 's' : ''}` : ''}{isTruncated ? ' (truncated)' : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
          {sqlQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 flex-shrink-0"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUpIcon className="h-3 w-3" />
              ) : (
                <ChevronDownIcon className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
        {!isExpanded && sqlQuery && (
          <p className="text-xs text-muted-foreground mt-1 font-mono truncate">
            {shortQuery}
          </p>
        )}
        {isExpanded && sqlQuery && (
          <pre className="text-xs bg-background p-2 rounded border overflow-x-auto mt-2 font-mono whitespace-pre-wrap">
            {sqlQuery}
          </pre>
        )}
      </div>
    </Card>
  );
};

export const ExecuteSqlQueryToolCall = ExecuteSqlQueryToolCallComponent as React.ComponentType<any>;
